import {
  isHtmlEffectivelyEmpty,
  type EmailFieldErrors,
} from '@kula-ai/components';
import type {
  InterviewCommunicationFormState,
  InterviewVariablesByCategory,
} from './communication.types';
import { SUBJECT_MAX_LENGTH } from './communication.types';

const REQUIRED_SELF_SCHEDULE_VARIABLE = 'interview.self_schedule_link';

export type InterviewCommunicationValidationErrors = {
  candidateCalendar?: EmailFieldErrors;
  candidateEmail?: EmailFieldErrors;
  interviewerCalendar?: EmailFieldErrors;
  interviewerEmail?: EmailFieldErrors;
  selfSchedule?: EmailFieldErrors;
};

/**
 * Variables context for validating template merge tags in editor content.
 * Each key maps to the variables for a specific communication block.
 */
export type CommunicationValidationVariables = {
  candidateCalendar?: InterviewVariablesByCategory;
  interviewerCalendar?: InterviewVariablesByCategory;
  candidateEmail?: InterviewVariablesByCategory;
  interviewerEmail?: InterviewVariablesByCategory;
  selfSchedule?: InterviewVariablesByCategory;
};

/** Optional flags — Schedule Interview manual/adhoc omits self-schedule UI but shares this validator. */
export type ValidateInterviewCommunicationOptions = {
  /**
   * When `false`, the self-schedule email block is not validated (Schedule Interview
   * manual/adhoc: that panel is not shown). Default `true` (Interview Activity wizard).
   */
  validateSelfSchedule?: boolean;
};

function subjectError(subject: string): string | undefined {
  const t = subject.trim();
  if (!t) {
    return 'Subject is required';
  }
  if (t.length > SUBJECT_MAX_LENGTH) {
    return `Subject must be at most ${SUBJECT_MAX_LENGTH} characters`;
  }
  return undefined;
}

/** Returns true if the content contains `{{…}}` tags that are not in the allowed variables list. */
function hasInvalidVariables(
  content: string,
  variables: InterviewVariablesByCategory | undefined
): boolean {
  if (!variables) {
    return false;
  }
  const allowedNames = Object.values(variables).flat();
  // While merge-variable queries are still loading, buckets default to `{}` and
  // `allowedNames` is empty — do not treat every `{{…}}` as invalid (blocks wizard).
  if (allowedNames.length === 0) {
    return false;
  }
  const matches = content.match(/{{\s*(.*?)\s*}}/g);
  if (!matches) {
    return false;
  }
  return matches.some(
    (tag) => !allowedNames.includes(tag.replace(/{{\s*|\s*}}/g, ''))
  );
}

function hasRequiredVariable(content: string, variableName: string): boolean {
  const pattern = new RegExp(`{{\\s*${variableName.replace('.', '\\.')}\\s*}}`);
  return pattern.test(content);
}

function validateEmailBlock(
  enabled: boolean,
  subject: string,
  content: string,
  from: string,
  variables?: InterviewVariablesByCategory
): EmailFieldErrors | undefined {
  if (!enabled) {
    return undefined;
  }
  const errors: EmailFieldErrors = {};
  const s = subjectError(subject);
  if (s) {
    errors.subject = s;
  } else if (hasInvalidVariables(subject, variables)) {
    errors.subject = 'Subject contains invalid variable(s)';
  }
  if (!from?.trim()) {
    errors.from = 'From is required';
  }
  if (isHtmlEffectivelyEmpty(content)) {
    errors.content = 'Body is required';
  } else if (hasInvalidVariables(content, variables)) {
    errors.content = 'Body contains invalid variable(s)';
  }
  return Object.keys(errors).length ? errors : undefined;
}

function validateCalendarBlock(
  content: string,
  variables?: InterviewVariablesByCategory
): EmailFieldErrors | undefined {
  if (isHtmlEffectivelyEmpty(content)) {
    return { content: 'Body is required' };
  }
  if (hasInvalidVariables(content, variables)) {
    return { content: 'Body contains invalid variable(s)' };
  }
  return undefined;
}

/**
 * Returns field errors for each block; all clear when the form is valid.
 */
export function validateInterviewCommunicationForm(
  state: InterviewCommunicationFormState,
  variables?: CommunicationValidationVariables,
  options?: ValidateInterviewCommunicationOptions
): InterviewCommunicationValidationErrors {
  const out: InterviewCommunicationValidationErrors = {};
  const shouldValidateSelfSchedule = options?.validateSelfSchedule !== false;

  const candCal = validateCalendarBlock(
    state.candidateCalendar.content,
    variables?.candidateCalendar
  );
  if (candCal) {
    out.candidateCalendar = candCal;
  }

  const intCal = validateCalendarBlock(
    state.interviewerCalendar.content,
    variables?.interviewerCalendar
  );
  if (intCal) {
    out.interviewerCalendar = intCal;
  }

  const candEmail = validateEmailBlock(
    state.candidateEmailEnabled,
    state.candidateEmail.subject,
    state.candidateEmail.content,
    state.candidateEmail.from,
    variables?.candidateEmail
  );
  if (candEmail) {
    out.candidateEmail = candEmail;
  }

  const intEmail = validateEmailBlock(
    state.interviewerEmailEnabled,
    state.interviewerEmail.subject,
    state.interviewerEmail.content,
    state.interviewerEmail.from,
    variables?.interviewerEmail
  );
  if (intEmail) {
    out.interviewerEmail = intEmail;
  }

  let selfErrors =
    shouldValidateSelfSchedule && state.selfScheduleEnabled
      ? validateEmailBlock(
          true,
          state.selfSchedule.subject,
          state.selfSchedule.content,
          state.selfSchedule.from,
          variables?.selfSchedule
        )
      : undefined;
  if (
    shouldValidateSelfSchedule &&
    state.selfScheduleEnabled &&
    !isHtmlEffectivelyEmpty(state.selfSchedule.content) &&
    !hasRequiredVariable(
      state.selfSchedule.content,
      REQUIRED_SELF_SCHEDULE_VARIABLE
    )
  ) {
    const existing = selfErrors ?? {};
    // Self-schedule link is mandatory. Keep this as the primary message so the
    // panel can render a clear, actionable hint instead of a generic invalid-variable error.
    existing.content = `Body must include {{${REQUIRED_SELF_SCHEDULE_VARIABLE}}}`;
    selfErrors = existing;
  }
  if (selfErrors) {
    out.selfSchedule = selfErrors;
  }

  return out;
}

export function hasValidationErrors(
  errors: InterviewCommunicationValidationErrors
): boolean {
  return Object.keys(errors).length > 0;
}
